import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Upload, FileText, Image as ImageIcon, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = (ct) => {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  return FileText;
};

export default function DocumentVault({ chestId }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", category: "general" });
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/hope-chests/${chestId}/documents`);
    setDocs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chestId]);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/hope-chests/${chestId}/upload?title=${encodeURIComponent(form.title || file.name)}&category=${encodeURIComponent(form.category)}`;
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setOpen(false); setForm({ title: "", category: "general" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/hope-chests/${chestId}/documents/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/files/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.title || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl">Document Vault</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="upload-document-button">
              <Upload className="w-4 h-4 mr-2" /> Upload
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Upload a document</DialogTitle>
              <DialogDescription>PDF, photos, or notes — up to 10 MB. Stored securely.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>File *</Label>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.txt,.csv,.doc,.docx" className="block mt-1.5 text-sm" data-testid="file-input" />
              </div>
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Mom's Medicare card" className="rounded-xl mt-1.5" data-testid="upload-title" />
              </div>
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1.5 rounded-xl border border-[#EBE5DF] px-3 py-2 bg-white">
                  <option value="general">General</option>
                  <option value="medical">Medical</option>
                  <option value="insurance">Insurance</option>
                  <option value="legal">Legal</option>
                  <option value="financial">Financial</option>
                  <option value="photo">Photo</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={busy} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="upload-submit">
                {busy ? "Uploading…" : "Upload"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {docs.length === 0 ? (
        <div className="hc-card p-10 text-center text-[#7A736E]" data-testid="empty-documents">
          No documents yet. Upload insurance cards, doctor's notes, or family photos.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => {
            const Icon = TYPE_ICON(d.content_type);
            return (
              <div key={d.id} className="hc-card p-5 flex gap-3" data-testid={`doc-${d.id}`}>
                <div className="w-12 h-12 rounded-2xl bg-[#F3EFEA] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#3A5A4A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.title}</p>
                  <p className="text-xs text-[#7A736E] capitalize">{d.category}</p>
                  <p className="text-xs text-[#7A736E] truncate">{d.original_filename}</p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => download(d)} className="text-xs text-[#3A5A4A] hover:underline inline-flex items-center gap-1" data-testid={`doc-download-${d.id}`}>
                      <Download className="w-3 h-3" /> Download
                    </button>
                    <button onClick={() => remove(d.id)} className="text-xs text-[#C87B67] hover:underline inline-flex items-center gap-1" data-testid={`doc-remove-${d.id}`}>
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
